import {
  GetCompanyCsrdVersionSchemaResponse,
  GetCsrdCompanyProductSegmentsRequestPathParams,
  GetCsrdCompanyProductSegmentsResponse,
  GetCsrdCompanyResultPartsRequestPathParams,
  GetCsrdCompanyResultPartsRequestQueryParams,
  GetCsrdCompanyResultPartsResponse,
  GetCsrdCompanyResultOfMatterRequestPathParams,
  GetCsrdCompanyResultOfMatterResponse,
  GetCsrdCompanyResultsRequestPathParams,
  GetCsrdCompanyResultsRequestQueryParams,
  GetCsrdCompanyResultsResponse,
  GetMinimalMaterialityResultForLeafMattersResponse,
  GetReasoningsVisibilityResponse,
  TriggerOnDemandMaterialityJobRequest,
  TriggerOnDemandMaterialityJobResponse,
  UpdateReasoningsVisibilityRequestPayload,
  zGetMinimalMaterialityResultForLeafMattersResponse,
} from '@upright/common/model/output/materiality/csrd-company-results/api-types';
import {
  DmaImpactEvidenceResponse,
  GenerateDmaImpactEvidenceRequestPathParams,
} from '@upright/common/model/output/materiality/csrd-company-results/impact-evidence/api-types';
import {
  DmaImpactStatementResponse,
  GenerateDmaImpactStatementRequestPathParams,
} from '@upright/common/model/output/materiality/csrd-company-results/impact-statement/api-types';
import { CompanyMaterialityExportSpreadsheet } from '@upright/common/model/output/materiality/csrd-company-results/types-export-data';
import {
  CompanyFinancialEffectsExportSpreadsheet,
  GetCompanyFinaEffectsExportExcelRequestQueryParams,
} from '@upright/common/model/output/materiality/csrd-company-results/financial-effects/types-export-data';
import { CsrdResultPartType } from '@upright/common/model/output/materiality/csrd-company-results/types-result-parts';
import {
  GetCompanyMaterialityMatrixRequestPathParams,
  GetCompanyMaterialityMatrixRequestQueryParams,
  GetCompanyMaterialityMatrixResponse,
} from '@upright/common/model/output/materiality/materiality-matrix/api-types';
import { appendSearchParamsToUrlPath } from '@upright/common/utils/url-management';
import {
  fetchAndValidateOrThrow,
  fetchOrThrow,
  fetchOrThrowBlob,
} from '@upright/ui/api/common/fetchers/fetch-or-throw';

export async function fetchMaterialityForVariant(
  pathParams: GetCsrdCompanyResultsRequestPathParams,
  queryParams: GetCsrdCompanyResultsRequestQueryParams,
) {
  const urlBase = `/materiality/company/${pathParams.variantId}`;
  const url = appendSearchParamsToUrlPath(urlBase, queryParams);
  return await fetchOrThrow<GetCsrdCompanyResultsResponse>(url);
}

export async function fetchCompanyCsrdResultOfMatter(pathParams: GetCsrdCompanyResultOfMatterRequestPathParams) {
  const urlBase = `/materiality/company/${pathParams.variantId}/matter/${pathParams.matterId}`;
  return await fetchOrThrow<GetCsrdCompanyResultOfMatterResponse>(urlBase);
}

export async function fetchCompanyCsrdVersionSchema(
  variantId: string,
  queryParams: GetCsrdCompanyResultsRequestQueryParams,
) {
  const urlBase = `/materiality/company/${variantId}/version-schema`;
  const url = appendSearchParamsToUrlPath(urlBase, queryParams);
  return await fetchOrThrow<GetCompanyCsrdVersionSchemaResponse>(url);
}

export async function fetchMaterialityParts<T extends CsrdResultPartType>(
  pathParams: GetCsrdCompanyResultPartsRequestPathParams,
  queryParams: GetCsrdCompanyResultPartsRequestQueryParams,
) {
  const urlBase = `/materiality/company/result-parts/${pathParams.variantId}`;
  const url = appendSearchParamsToUrlPath(urlBase, queryParams);
  return await fetchOrThrow<GetCsrdCompanyResultPartsResponse<T>>(url);
}

export async function fetchCsrdCompanyProductSegments(pathParams: GetCsrdCompanyProductSegmentsRequestPathParams) {
  return await fetchOrThrow<GetCsrdCompanyProductSegmentsResponse>(
    `/materiality/company/product-segments/${pathParams.variantId}`,
  );
}

export async function fetchCompanyMaterialityMatrixData(
  pathParams: GetCompanyMaterialityMatrixRequestPathParams,
  queryParams: GetCompanyMaterialityMatrixRequestQueryParams,
) {
  const urlBase = `/materiality/company/matrix/${pathParams.variantId}`;
  const url = appendSearchParamsToUrlPath(urlBase, queryParams);
  return await fetchOrThrow<GetCompanyMaterialityMatrixResponse>(url);
}

export async function triggerOnDemandMaterialityAssessmentJob(payload: TriggerOnDemandMaterialityJobRequest) {
  return await fetchOrThrow<TriggerOnDemandMaterialityJobResponse>(
    `/materiality/company/on-demand/job`,
    'POST',
    payload,
  );
}

export async function postReasoningsVisibility(entityId: string, payload: UpdateReasoningsVisibilityRequestPayload) {
  const url = `/materiality/company/reasonings-visibility/${entityId}`;
  return await fetchOrThrow<GetReasoningsVisibilityResponse>(url, 'POST', payload);
}

export async function fetchReasoningsVisibility(entityId: string) {
  const url = `/materiality/company/reasonings-visibility/${entityId}`;
  return await fetchOrThrow<GetReasoningsVisibilityResponse>(url, 'GET');
}

export async function fetchCompanyCsrdExportSpreadsheetData(params: { variantId: string }) {
  return await fetchOrThrow<CompanyMaterialityExportSpreadsheet>(
    `/materiality/company/${params.variantId}/export-excel`,
    'GET',
  );
}

export async function fetchCompanyCsrdExportJsonData(params: { variantId: string }): Promise<Blob> {
  return await fetchOrThrowBlob(`/materiality/company/${params.variantId}/export-json`, 'GET');
}

export async function fetchCompanyFinancialEffectsExportSpreadsheetData(params: {
  variantId: string;
  query: GetCompanyFinaEffectsExportExcelRequestQueryParams;
}) {
  const urlBase = `/materiality/company/${params.variantId}/financial-effects/export-excel`;
  const url = appendSearchParamsToUrlPath(urlBase, params.query);
  return await fetchOrThrow<CompanyFinancialEffectsExportSpreadsheet>(url, 'GET');
}

export async function fetchCompanyMinimalMaterialityResults(
  pathParams: GetCsrdCompanyResultsRequestPathParams,
  queryParams: GetCsrdCompanyResultsRequestQueryParams,
) {
  const urlBase = `/materiality/company/${pathParams.variantId}/minimal-results`;
  const url = appendSearchParamsToUrlPath(urlBase, queryParams);
  return await fetchAndValidateOrThrow<GetMinimalMaterialityResultForLeafMattersResponse>({
    path: url,
    schema: zGetMinimalMaterialityResultForLeafMattersResponse,
  });
}

/**
 * Get-or-generate the per-impact ESRS reporting-ready "Impact summary" (PLT-4823; caching
 * PLT-5676). The statement is served from the `variant_dma_impact_statement` cache and only
 * regenerated when the prompt version bumps.
 *
 * `force` (PLT-5744, team users only) bypasses the cache and regenerates the statement,
 * overwriting the cached row — used by the internal refresh control so a summary can be
 * refreshed after the underlying results are iterated.
 */
export async function generateCompanyDmaImpactStatement(
  pathParams: GenerateDmaImpactStatementRequestPathParams,
  force = false,
) {
  const { variantId, matterId, valence, clientId } = pathParams;
  const query = force ? '?force=true' : '';
  const url = `/materiality/company/${variantId}/dma-impact-statement/${encodeURIComponent(
    matterId,
  )}/${encodeURIComponent(valence)}/${encodeURIComponent(clientId)}/generate${query}`;
  return await fetchOrThrow<DmaImpactStatementResponse>(url, 'POST');
}

/**
 * Get-or-generate the per-impact generated scientific evidence shown in the DMA "Scientific
 * support" tab (PLT-5875). Team-only on the backend while the output is being evaluated.
 *
 * The selection is served from the `variant_dma_impact_evidence` cache; `force` bypasses it and
 * re-runs the search + selection, overwriting the cached row — used by the internal refresh
 * control after contributing products or the article pipeline have been iterated.
 */
export async function generateCompanyDmaImpactEvidence(
  pathParams: GenerateDmaImpactEvidenceRequestPathParams,
  force = false,
) {
  const { variantId, matterId, valence, clientId } = pathParams;
  const query = force ? '?force=true' : '';
  const url = `/materiality/company/${variantId}/dma-impact-evidence/${encodeURIComponent(
    matterId,
  )}/${encodeURIComponent(valence)}/${encodeURIComponent(clientId)}/generate${query}`;
  return await fetchOrThrow<DmaImpactEvidenceResponse>(url, 'POST');
}
